import { CustomerAddressCard } from "@/services/backend/addresses/components/customer-address-card";
import { ServiceAddressCard } from "@/services/backend/addresses/components/service-address-card";
import { ContactCard } from "@/services/backend/contacts/components/contact-card";
import { RepresentativeCard } from "@/services/backend/representatives/components/representative-card";
import { Address } from "@/services/backend/addresses/types/address";
import { Contact } from "@/services/backend/contacts/types/contact";
import { Representative } from "@/services/backend/representatives/types/representative";
import {
  Order,
  updatePostalAddress,
  updateRepresentative,
  updateServiceAddress,
} from "@/services/backend/htz/orders/types/order";
import t from "@/lang/lang";
import {
  fieldErrorsForPrefix,
  rtkErrIsValidationError,
} from "@/shared/lib/errors/validation-error";
import { useParams } from "react-router";
import {
  resetField,
  useErrDocumentNotReady,
} from "@/shared/bsm/core/document/components/ui/err-document-not-ready";
import {
  useHtzOrderPutMutation,
  useHtzOrderShowQuery,
} from "@/services/backend/htz/orders/api/order-api";
import { useState } from "react";
import { useDebouncedMutation } from "@/shared/lib/debounce/debounce";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { Card } from "@/shared/components/ui/card";
import { useRefEffect } from "@/shared/hooks/use-ref-effect";

export function AddressesAndContactsRoute() {
  const { orderId } = useParams();
  const {
    data: order,
    isLoading,
    error,
  } = useHtzOrderShowQuery({
    id: orderId!,
  });

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  if (isLoading) {
    return (
      <Card className="text-muted-foreground animate-pulse p-6">
        {t("Lade Daten...")}
      </Card>
    );
  }

  return <AddressesAndContacts order={order!} />;
}

function AddressesAndContacts({ order }: { order: Order }) {
  const { notReadyError, setNotReadyError } = useErrDocumentNotReady();

  const [request, setRequest] = useState({ ...order });
  const mutation = useHtzOrderPutMutation();
  const { error } = useDebouncedMutation(request, setRequest, mutation, {
    ...order,
  });

  useRefEffect(order.offerId, () => {
    setRequest(order);
  });

  const validationError =
    notReadyError ?? (rtkErrIsValidationError(error) ? error.data : null);

  const resetValidationErrorField = (prefix: string) => (field: string) => {
    const prefixedField = `${prefix}${field}`;
    setNotReadyError(resetField(notReadyError, prefixedField));
  };

  const onCustomerAddressChange = (customerAddress: Address) => {
    setRequest(updatePostalAddress(request, customerAddress));
  };

  const onServiceAddressChange = (serviceAddress: Address) => {
    setRequest(updateServiceAddress(request, serviceAddress));
  };

  const onContactChange = (contact: Contact) => {
    setRequest({ ...request, contact });
  };

  const onRepresentativeChange = (representative: Representative) => {
    setRequest(updateRepresentative(request, representative));
  };

  return (
    <div className="grid gap-2 xl:grid-cols-2">
      <CustomerAddressCard
        customerAddress={request.postalAddress}
        onCustomerAddressChange={onCustomerAddressChange}
        description={t(
          "Adresse an welche der Auftragseingang geschickt werden soll.",
        )}
        fieldErrors={fieldErrorsForPrefix(validationError, "postalAddress.")}
        resetFieldError={resetValidationErrorField("postalAddress.")}
      />
      <ServiceAddressCard
        address={request.serviceAddress}
        onAddressChange={onServiceAddressChange}
        fieldErrors={fieldErrorsForPrefix(validationError, "serviceAddress.")}
        resetFieldError={resetValidationErrorField("serviceAddress.")}
      />
      <ContactCard
        contact={request.contact}
        onContactChange={onContactChange}
      />
      <RepresentativeCard
        representative={request.representative}
        onRepresentativeChange={onRepresentativeChange}
        fieldErrors={fieldErrorsForPrefix(validationError, "representative.")}
        resetFieldError={resetValidationErrorField("representative.")}
      />
    </div>
  );
}
